"use client";

import { useEffect, useRef, useState } from "react";

/**
 * A single frosted review card in the testimonial wall.
 *
 * Cards are a consistent size, so a long quote is clipped and gains a down
 * arrow that opens the rest in place. The arrow only appears when the
 * quote actually overflows, measured against the collapsed box (so a card
 * that fits never shows a control that would do nothing). Overflow is only
 * re-measured while collapsed: once open, clientHeight equals scrollHeight
 * and re-measuring would wrongly conclude there is nothing to expand.
 *
 * Nothing here animates position; expanding grows this card only, and the
 * grid places cards independently (align-items: start), so its neighbours
 * do not move.
 */
export function ReviewCard({
  quote,
  author,
  index,
  className = "",
}: {
  quote: string;
  author: string;
  index: number;
  className?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);
  const quoteRef = useRef<HTMLQuoteElement>(null);

  useEffect(() => {
    if (expanded) return;
    const el = quoteRef.current;
    if (!el) return;
    const check = () => setCanExpand(el.scrollHeight > el.clientHeight + 2);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [expanded]);

  return (
    <figure className={`flex flex-col ${className}`}>
      <div
        className={`silk-review-card flex flex-col border border-white/50 px-7 py-10 sm:px-8 ${
          expanded ? "h-auto" : "h-[26rem]"
        }`}
      >
        <span
          className="font-serif text-gold-deep text-3xl leading-none block"
          aria-hidden="true"
        >
          &ldquo;
        </span>

        <blockquote
          ref={quoteRef}
          className={`mt-3 min-h-0 flex-1 font-serif italic text-ink/85 text-[14px] sm:text-[15px] leading-[1.65] ${
            expanded ? "" : "overflow-hidden"
          }`}
        >
          {quote}
        </blockquote>

        <div className="mt-5 flex items-center justify-between gap-3">
          <figcaption className="flex items-center gap-3">
            <span className="block h-px w-7 bg-gold" />
            <span className="font-mono text-gold-deep text-[10px] tracking-[0.25em] uppercase font-medium">
              {author}
            </span>
          </figcaption>

          {canExpand && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              aria-label={
                expanded
                  ? `Collapse review from ${author}`
                  : `Read the full review from ${author}`
              }
              className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border border-ink/20 text-ink/60 transition-colors hover:border-gold hover:text-gold-deep"
            >
              <svg
                width="12"
                height="8"
                viewBox="0 0 12 8"
                fill="none"
                aria-hidden="true"
                className={`transition-transform duration-300 ${
                  expanded ? "rotate-180" : ""
                }`}
              >
                <path
                  d="M1 1.5L6 6.5L11 1.5"
                  stroke="currentColor"
                  strokeWidth="1.25"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
        </div>
      </div>

      <span
        aria-hidden="true"
        className="mt-3 block pl-0.5 text-left font-mono text-[10px] tracking-[0.22em] text-ink/45"
      >
        ( {index} )
      </span>
    </figure>
  );
}
